import React from 'react';
import { path, prop } from 'ramda';

import { ReCaptchaCheckUserName } from '@interlayer/js/app/components/main/Registration/components/common/ReCaptchaCheckUserName';
import { EmailJoinField } from '@interlayer/js/app/components/main/Registration/components/fields';

import { Button } from 'js/components/common/Button';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import Form from 'js/components/common/Form';

import { useValidationRules } from '@interlayer/js/app/hooks/useValidationRules';
import { CUSTOM_VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { useFetchWithTwoFa } from '@interlayer/js/components/popups/TwoFactorAuthenticationPopup/buzz/hooks/useFetchWithTwoFa';

import { useTranslations } from 'js/hooks/i18n';
import { useFormatter } from 'js/hooks/useFormatter';
import { useFetchUserDetails } from 'js/hooks/user';
import { useUserSelector } from 'js/store/hooks';

import { changeEmail, changeUserContactPreferences } from './server';

import style from './EmailChange.scss';

const VALIDATION_FIELDS_EMAIL_CHANGE = [CUSTOM_VALIDATION_FIELDS.duplicate];

export const EmailChange = ({ closePopup }) => {
    const { getDateForIms } = useFormatter();
    const fetchWithTwoFa = useFetchWithTwoFa();
    const fetchUserDetails = useFetchUserDetails();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const contactPreferences = useUserSelector(path(['userDetails', 'contactPreferences']));
    const translate = useTranslations('playtech.mobile.system.changeemail.');
    const promoEmail = contactPreferences?.find(v => v.channel === 'email' && v.type === 'promotional').preference || false;
    const { isShowReCaptcha } = useRegistrationSelector();
    const validationConfig = useValidationRules(VALIDATION_FIELDS_EMAIL_CHANGE);

    const onSubmit = data => {
        if (!isLoggedIn) {
            closePopup();
            return;
        }

        const { email } = data;

        return fetchWithTwoFa({
            fetch: () => changeEmail({ email, changeTimestamp: getDateForIms(new Date()) }),
            afterFetch: () => changeUserContactPreferences([{
                channel: 'email',
                isInvalid: false,
                preference: data.promoEmail,
                type: 'promotional'
            }]).then(() => {
                fetchUserDetails();
                closePopup();
            })
        });
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={{ promoEmail }}
            validationConfig={validationConfig}
            render={({ submitting, hasValidationErrors, submitError, handleSubmit }) => (
                <form
                    className={style.form}
                    onSubmit={event => event.preventDefault()}
                >
                    {submitError && <ErrorMessage error={submitError} animate isFormError />}

                    <span className={style.label}>{translate('label.top')}</span>

                    {isShowReCaptcha
                        ? (
                            <ReCaptchaCheckUserName />
                        )
                        : (
                            <EmailJoinField
                                autoCapitalize="none"
                                className={style.field}
                                classNames={{ control: style.control }}
                                placeholder={translate('placeholder.email')}
                            />
                        )
                    }

                    <span className={style.label}>{translate('label.middle')}</span>

                    <Checkbox
                        kind="toggle"
                        type="checkbox"
                        name="promoEmail"
                        label={translate('input.promotion.label')}
                        className={style.rememberMe}
                        classNames={{ checkbox: style.checkbox, control: style.control }}
                    />

                    <span className={style.label}>{translate('label.bottom')}</span>

                    <span className={style.separator}>{translate('separator')}</span>

                    <span className={style.over18Icon} />

                    <div className={style.controls}>
                        <Button
                            data-testid="EmailChange__submit"
                            onClick={handleSubmit}
                            className={style.submit}
                            disabled={hasValidationErrors || submitting}
                        >
                            {translate('cta')}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};
